import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2, UploadCloud, X, FileText, Info } from "lucide-react";
import { InspirationCarousel } from "./InspirationCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDON_OPTIONS = [
  { name: "Enquiry Form", tip: "A live contact form built directly into your website — visitors can fill it in and submit instantly, and their message lands straight in your inbox." },
  { name: "FAQ Section", tip: "A dedicated section on your website where you can answer your most common customer questions upfront. Customers get instant answers without having to call — meaning the only calls you receive are from people ready to book." },
];
const ACCEPT = ".jpg,.jpeg,.png,.pdf,.svg,.webp,.gif";
const MAX_FILES = 10;
const MAX_SIZE = 15 * 1024 * 1024;

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  business_name: z.string().min(1, "Please enter your business name"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(5, "Please enter your phone number"),
  business_description: z.string().min(5, "Please tell us a little about your business"),
  visitors_goal: z.string().optional(),
  brand_story: z.string().optional(),
  look_feel: z.string().optional(),
  company: z.string().optional(), // honeypot
});

const InfoTip = ({ text, label, slug }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex shrink-0"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-label={`More information about ${label}`}
        onClick={() => setOpen((o) => !o)} data-testid={`addon-info-${slug}`}
        className="text-[#7F92AE] hover:text-[#39D5FF] focus:text-[#39D5FF] transition grid place-items-center w-6 h-6">
        <Info size={16} />
      </button>
      {open && (
        <span role="tooltip"
          className="absolute z-50 bottom-full right-0 mb-2 w-64 rounded-xl bg-[#06152D] border border-[rgba(65,176,255,0.55)] shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_20px_rgba(0,168,255,0.15)] px-3.5 py-2.5 text-xs text-[#AEBFD8] leading-relaxed">
          {text}
        </span>
      )}
    </span>
  );
};

const Field = ({ label, name, register, error, type = "text", ...rest }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-semibold text-[#AEBFD8] mb-1.5">{label}</label>
    <input id={name} type={type} {...register(name)} {...rest} aria-invalid={!!error}
      className="w-full bg-[#06152D]/80 border border-[rgba(67,157,255,0.35)] rounded-xl px-4 py-3 text-[#F7FAFF] placeholder-[#7F92AE] focus:outline-none focus:border-[#39D5FF] focus:ring-2 focus:ring-[#39D5FF]/30 transition-all min-h-[48px]" />
    {error && <p className="mt-1.5 text-xs text-[#ff6b6b]" role="alert" data-testid={`error-${name}`}>{error.message}</p>}
  </div>
);

const TextArea = ({ label, name, register, error, ...rest }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-semibold text-[#AEBFD8] mb-1.5">{label}</label>
    <textarea id={name} rows={4} {...register(name)} {...rest} aria-invalid={!!error}
      className="w-full bg-[#06152D]/80 border border-[rgba(67,157,255,0.35)] rounded-xl px-4 py-3 text-[#F7FAFF] placeholder-[#7F92AE] focus:outline-none focus:border-[#39D5FF] focus:ring-2 focus:ring-[#39D5FF]/30 transition-all resize-none" />
    {error && <p className="mt-1.5 text-xs text-[#ff6b6b]" role="alert" data-testid={`error-${name}`}>{error.message}</p>}
  </div>
);

export const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");
  const [addons, setAddons] = useState([]);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });

  const toggleAddon = (a) =>
    setAddons((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const addFiles = (list) => {
    setFileError("");
    const incoming = Array.from(list);
    let next = [...files];
    for (const f of incoming) {
      if (f.size > MAX_SIZE) { setFileError(`${f.name} is larger than 15MB.`); continue; }
      if (!next.some((e) => e.name === f.name && e.size === f.size)) next.push(f);
    }
    if (next.length > MAX_FILES) { setFileError(`You can upload up to ${MAX_FILES} files.`); next = next.slice(0, MAX_FILES); }
    setFiles(next);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const onSubmit = async (data) => {
    setServerError("");
    try {
      const fd = new FormData();
      fd.append("full_name", data.full_name);
      fd.append("business_name", data.business_name);
      fd.append("email", data.email);
      fd.append("phone", data.phone);
      fd.append("business_description", data.business_description);
      fd.append("visitors_goal", data.visitors_goal || "");
      fd.append("brand_story", data.brand_story || "");
      fd.append("look_feel", data.look_feel || "");
      fd.append("addons", JSON.stringify(addons));
      fd.append("company", data.company || "");
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/leads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      reset();
      setAddons([]);
      setFiles([]);
    } catch (e) {
      setServerError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="glass-card p-8 h-full flex flex-col items-center justify-center text-center py-12" data-testid="form-success">
        <span className="grid place-items-center w-16 h-16 rounded-full bg-[rgba(69,224,181,0.15)] border border-[rgba(69,224,181,0.5)] mb-5">
          <CheckCircle2 size={32} className="text-[#45E0B5]" />
        </span>
        <h3 className="text-2xl font-bold text-[#F7FAFF]">Thank you!</h3>
        <p className="mt-3 text-[#AEBFD8] max-w-sm">
          Your enquiry has been received. We'll be in touch shortly to arrange a friendly chat.
        </p>
        <button onClick={() => setSubmitted(false)} data-testid="form-reset"
          className="btn-secondary mt-7 rounded-full px-6 py-3 text-sm font-semibold min-h-[44px]">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="glass-card p-6 md:p-8 space-y-4" noValidate data-testid="consultation-form">
      <Field label="Full Name" name="full_name" register={register} error={errors.full_name} placeholder="Jane Smith" autoComplete="name" />
      <Field label="Business Name" name="business_name" register={register} error={errors.business_name} placeholder="Your business" autoComplete="organization" />
      <Field label="Email Address" name="email" register={register} error={errors.email} placeholder="jane@business.com" autoComplete="email" />
      <Field label="Phone Number" name="phone" register={register} error={errors.phone} type="tel" placeholder="07123 456789" autoComplete="tel" />

      <TextArea label="Tell us about your business — what do you do and who are your customers?"
        name="business_description" register={register} error={errors.business_description}
        placeholder="A short description of your business and customers…" />

      <InspirationCarousel />

      <TextArea label="What do you want visitors to do when they land on your site? (optional)"
        name="visitors_goal" register={register} error={errors.visitors_goal} rows={6}
        placeholder="e.g. call you, fill out a form, browse your services" />

      <TextArea label="Describe your brand and what makes your business different (optional)"
        name="brand_story" register={register} error={errors.brand_story} rows={6}
        placeholder="your story, your customers, what sets you apart" />

      <TextArea label="How do you want it to look and feel? (optional)"
        name="look_feel" register={register} error={errors.look_feel} rows={6}
        placeholder="Reference any of the inspiration images above, colours, styles, anything you definitely don't want" />

      {/* Add-ons */}
      <fieldset>
        <legend className="block text-sm font-semibold text-[#AEBFD8] mb-2">Which add-ons are you interested in?</legend>
        <div className="grid sm:grid-cols-2 gap-3">
          {ADDON_OPTIONS.map((a) => {
            const slug = a.name.replace(/\s+/g, "-").toLowerCase();
            const checked = addons.includes(a.name);
            return (
              <div key={a.name}
                className={`flex items-center gap-2 rounded-xl px-4 py-3 border transition min-h-[48px] ${
                  checked ? "border-[#39D5FF] bg-[rgba(0,168,255,0.12)]" : "border-[rgba(67,157,255,0.35)] bg-[#06152D]/80 hover:border-[rgba(65,176,255,0.55)]"
                }`}>
                <label data-testid={`addon-${slug}`} className="flex items-center gap-3 flex-1 cursor-pointer min-w-0">
                  <input type="checkbox" checked={checked} onChange={() => toggleAddon(a.name)}
                    className="w-5 h-5 rounded accent-[#00A8FF] shrink-0" />
                  <span className="text-sm font-medium text-[#F7FAFF] truncate">{a.name}</span>
                </label>
                <InfoTip text={a.tip} label={a.name} slug={slug} />
              </div>
            );
          })}
        </div>
      </fieldset>

      {/* File upload */}
      <div>
        <label htmlFor="assets" className="block text-sm font-semibold text-[#AEBFD8] mb-2">
          Upload your assets — logo, images, branding, or anything else you'd like us to use
        </label>
        <label htmlFor="assets"
          className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[rgba(65,176,255,0.55)] bg-[#06152D]/60 px-4 py-7 cursor-pointer hover:bg-[#06152D]/90 hover:border-[#39D5FF] transition text-center">
          <UploadCloud size={26} className="text-[#39D5FF]" />
          <span className="text-sm text-[#AEBFD8]"><span className="text-[#39D5FF] font-semibold">Click to upload</span> or drag and drop</span>
          <span className="text-xs text-[#7F92AE]">JPG, PNG, PDF, SVG · up to 15MB each</span>
          <input id="assets" type="file" multiple accept={ACCEPT} className="hidden" data-testid="file-upload"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </label>
        {fileError && <p className="mt-1.5 text-xs text-[#ff6b6b]" role="alert" data-testid="error-files">{fileError}</p>}
        {files.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="file-list">
            {files.map((f, i) => (
              <li key={i} className="flex items-center gap-3 bg-[#06152D]/80 border border-[rgba(67,157,255,0.35)] rounded-lg px-3 py-2">
                <FileText size={16} className="text-[#39D5FF] shrink-0" />
                <span className="text-sm text-[#F7FAFF] truncate flex-1">{f.name}</span>
                <span className="text-xs text-[#7F92AE] shrink-0">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${f.name}`}
                  className="text-[#7F92AE] hover:text-[#ff6b6b] transition shrink-0" data-testid={`remove-file-${i}`}>
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Honeypot */}
      <input type="text" tabIndex={-1} autoComplete="off" {...register("company")} className="hidden" aria-hidden="true" />

      {serverError && <p className="text-sm text-[#ff6b6b]" role="alert" data-testid="form-server-error">{serverError}</p>}

      <button type="submit" disabled={isSubmitting} data-testid="contact-form-submit"
        className="btn-primary w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-bold min-h-[48px] disabled:opacity-70">
        {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Book a Free Consultation <ArrowRight size={18} /></>}
      </button>
    </form>
  );
};
